import { useState } from "react";
import { ToolPageLayout } from "@/components/ToolPageLayout";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Textarea } from "@/components/ui/Textarea";
import { 
  CheckCircle2, 
  AlertTriangle, 
  XCircle, 
  Globe, 
  FileText, 
  Sparkles, 
  RefreshCw, 
  Clipboard, 
  Check, 
  ExternalLink, 
  BookOpen, 
  Search,
  ChevronRight,
  ShieldCheck,
  AlertCircle,
  Code
} from "lucide-react";
import { motion } from "framer-motion";

export function AdSenseAuditor() {
  // Domain & Niche Inputs
  const [domain, setDomain] = useState("myawesomeblog.com");
  const [siteNiche, setSiteNiche] = useState("technology");
  
  // Tab/Step state
  const [activeTab, setActiveTab] = useState<"audit" | "report" | "privacy-gen" | "ads-txt" | "policies">("audit");

  // Detailed Questionnaire State
  // Content Quality
  const [contentOrigin, setContentOrigin] = useState<"original" | "ai" | "curated" | "scraped">("original");
  const [wordCount, setWordCount] = useState<"high" | "medium" | "low">("high"); // >800, 400-800, <400
  const [articleCount, setArticleCount] = useState<"high" | "medium" | "low">("medium"); // >30, 10-30, <10
  const [restrictedNiche, setRestrictedNiche] = useState<boolean>(false); // Adult, hacking, weapons, etc.
  const [hasPlaceholderText, setHasPlaceholderText] = useState<boolean>(false); // Lorem ipsum left over

  // UX & Navigation
  const [hasMenu, setHasMenu] = useState<boolean>(true);
  const [hasBrokenLinks, setHasBrokenLinks] = useState<boolean>(false);
  const [hasSearch, setHasSearch] = useState<boolean>(true);
  const [isMobileResponsive, setIsMobileResponsive] = useState<boolean>(true);

  // Legal Pages
  const [hasPrivacyPolicy, setHasPrivacyPolicy] = useState<boolean>(false);
  const [hasTerms, setHasTerms] = useState<boolean>(false);
  const [hasContact, setHasContact] = useState<boolean>(false);
  const [hasAbout, setHasAbout] = useState<boolean>(false);

  // Technical & Traffic
  const [isCustomDomain, setIsCustomDomain] = useState<boolean>(true);
  const [isHttps, setIsHttps] = useState<boolean>(true);
  const [trafficSource, setTrafficSource] = useState<"search" | "social" | "paid" | "bots">("search");
  const [siteAge, setSiteAge] = useState<"old" | "medium" | "new">("medium"); // >6 mo, 1-6 mo, <1 mo

  // Auditor Run State
  const [isAuditing, setIsAuditing] = useState<boolean>(false);
  const [auditProgress, setAuditProgress] = useState<number>(0);
  const [auditMessage, setAuditMessage] = useState<string>("");

  // Privacy Policy Generator state
  const [siteName, setSiteName] = useState("My Awesome Blog");
  const [ownerName, setOwnerName] = useState("Awesome Media Group Inc.");
  const [contactEmail, setContactEmail] = useState("contact@myawesomeblog.com");
  const [includeGdpr, setIncludeGdpr] = useState(true);
  const [generatedPrivacyText, setGeneratedPrivacyText] = useState("");
  const [isCopied, setIsCopied] = useState(false);

  // Ads.txt Generator state
  const [pubId, setPubId] = useState("pub-1234567890123456");
  const [adsTxtCopied, setAdsTxtCopied] = useState(false);

  // Copy helpers
  const handleCopy = (text: string, type: "privacy" | "adstxt") => {
    navigator.clipboard.writeText(text);
    if (type === "privacy") {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } else {
      setAdsTxtCopied(true);
      setTimeout(() => setAdsTxtCopied(false), 2000);
    }
  };

  // Run simulated audit
  const runAudit = () => {
    setIsAuditing(true);
    setAuditProgress(0);
    const messages = [
      "Connecting to site headers...",
      "Inspecting SSL cert and protocol hierarchy...",
      "Analyzing content originality indexes and keyword density...",
      "Checking mobile responsiveness viewport tags...",
      "Scanning navigation menus and searching for broken linkages...",
      "Verifying required legal policies & cookies disclosures...",
      "Evaluating traffic origins and potential spam signatures...",
      "Finalizing comprehensive AdSense compliance report card..."
    ];

    let step = 0;
    const interval = setInterval(() => {
      step++;
      setAuditProgress((step / messages.length) * 100);
      setAuditMessage(messages[step - 1] || "Finished!");
      if (step >= messages.length) {
        clearInterval(interval);
        setIsAuditing(false);
        setActiveTab("report");
      }
    }, 450);
  };

  // Calculate results based on form states
  const calculateAuditResults = () => {
    let score = 100;
    const passed: { title: string; desc: string }[] = [];
    const warnings: { title: string; desc: string; fix: string }[] = [];
    const criticalFails: { title: string; desc: string; fix: string }[] = [];

    // --- Content Quality ---
    if (restrictedNiche) {
      score -= 35;
      criticalFails.push({
        title: "Unsupported or Restricted Niche Content",
        desc: "Your site contains topics listed under Google Publisher Restrictions (e.g. adult, illegal, hacking, or medical advice without authoritative backing).",
        fix: "Remove or completely quarantine content that violates publisher guidelines before re-applying."
      });
    } else {
      passed.push({
        title: "Clean Safe Content Topic",
        desc: "Your primary niche matches Google's standard family-safe publishing categories."
      });
    }

    if (contentOrigin === "scraped") {
      score -= 40;
      criticalFails.push({
        title: "Scraped or Copyrighted Content",
        desc: "Your text content appears to be copied, translated, or aggregated from existing sites without adding significant proprietary value.",
        fix: "AdSense demands unique, original value. Rewrite low-value posts or author high-quality original analysis."
      });
    } else if (contentOrigin === "ai") {
      score -= 15;
      warnings.push({
        title: "Heavily AI-Generated Content Detected",
        desc: "Raw, unmodified machine-generated text may be flagged as low-value, thin content if it doesn't solve specific user queries.",
        fix: "Manually rewrite articles to add personal experiences, unique reviews, and structured data, rather than publishing raw LLM templates."
      });
    } else {
      passed.push({
        title: "Original Human Authorship",
        desc: "Your content represents unique authorship, lowering the risk of 'Valueless Inventory' rejection."
      });
    }

    if (wordCount === "low") {
      score -= 15;
      warnings.push({
        title: "Thin Article Word Count",
        desc: "A majority of your articles are under 400 words. AdSense filters flag sites with thin text blocks.",
        fix: "Ensure your primary articles average 600-1000+ words with deep coverage of the target topic."
      });
    } else if (wordCount === "medium") {
      passed.push({
        title: "Satisfactory Post Length",
        desc: "Article lengths are acceptable, but deeper guides are recommended for higher acceptance odds."
      });
    } else {
      passed.push({
        title: "High-Quality Long-Form Text Density",
        desc: "Your average article length supports high textual richness."
      });
    }

    if (articleCount === "low") {
      score -= 20;
      criticalFails.push({
        title: "Insufficient Content Volume",
        desc: "Your site has fewer than 10 published articles. AdSense usually rejects sites for 'Insufficient Content' if the index is too tiny.",
        fix: "Publish at least 25-30 premium, fully written posts before initiating a review."
      });
    } else if (articleCount === "medium") {
      warnings.push({
        title: "Borderline Article Volume",
        desc: "You have between 10 and 30 posts. Some reviewers reject sites with under 20-30 active pages.",
        fix: "Aim to publish 5-10 additional search-optimized articles to secure your catalog structure."
      });
    } else {
      passed.push({
        title: "Robust Article Catalog Size",
        desc: "Your site has plenty of content to populate standard ad spots."
      });
    }

    if (hasPlaceholderText) {
      score -= 15;
      criticalFails.push({
        title: "Placeholder Template Content Present",
        desc: "Found leftovers of 'Lorem Ipsum' or default template configurations on active pages.",
        fix: "Clean up all draft/test files and remove any standard template filler text entirely from sidebars, headers, and post content."
      });
    }

    // --- Legal pages (A HUGE DEAL in AdSense) ---
    if (!hasPrivacyPolicy) {
      score -= 25;
      criticalFails.push({
        title: "Missing or Invalid Privacy Policy Page",
        desc: "AdSense requires a compliant Privacy Policy explicitly explaining how third-party vendors (like Google) serve personalized cookies.",
        fix: "Create a page titled 'Privacy Policy'. Use our generator tab to output AdSense-compliant cookie clauses, publish it, and add it to your main footer."
      });
    } else {
      passed.push({
        title: "Privacy Policy Present",
        desc: "Your site lists a Privacy Policy, protecting user transparency."
      });
    }

    if (!hasContact) {
      score -= 10;
      warnings.push({
        title: "Missing Contact Page",
        desc: "AdSense reviews look for clear avenues of communication to establish your site as a genuine commercial publication.",
        fix: "Add a Contact page containing a contact form, professional email address, or social media links in the main navigation."
      });
    } else {
      passed.push({
        title: "Contact Page Active",
        desc: "Users can easily reach out to you, reinforcing domain trust."
      });
    }

    if (!hasAbout) {
      score -= 10;
      warnings.push({
        title: "Missing About Us / Editorial Bio Page",
        desc: "Without an About page, reviewers can't evaluate the expertise or authorization of the publisher (E-E-A-T guidelines).",
        fix: "Create a detailed About page showing who owns/runs the site, the site's editorial mission, and brief bios."
      });
    } else {
      passed.push({
        title: "About Us Page Configured",
        desc: "E-E-A-T parameters are reinforced by presenting your publisher bio."
      });
    }

    if (!hasTerms) {
      score -= 5;
      warnings.push({
        title: "Missing Terms of Service Page",
        desc: "Terms of Service pages outline usage boundaries and prevent legal liabilities.",
        fix: "Add a standard Terms of Service page to complete the trust footer."
      });
    }

    // --- UX & Technical ---
    if (!hasMenu) {
      score -= 20;
      criticalFails.push({
        title: "Poor Navigation Structure",
        desc: "Your site lacks a clear, accessible header menu, making it difficult for visitors or bots to discover content.",
        fix: "Create an organized menu at the top of your layout linking to key categories, legal pages, and latest posts."
      });
    } else {
      passed.push({
        title: "Clear Header Navigation Menu",
        desc: "Your header menu enables rapid crawling and flawless search bot routing."
      });
    }

    if (hasBrokenLinks) {
      score -= 10;
      warnings.push({
        title: "Broken Hyperlinks or Dead Buttons",
        desc: "Broken links trigger 'Site down or unavailable' or poor quality flags during manual human evaluations.",
        fix: "Use a free broken link validator tool to clear out 404 targets in internal copy or sidebar templates."
      });
    }

    if (!isMobileResponsive) {
      score -= 20;
      criticalFails.push({
        title: "Unresponsive Mobile Layout",
        desc: "Your site fails mobile optimization, leading to poor user behavior and ad placement conflicts.",
        fix: "Switch to a modern, fully responsive Tailwind/Bootstrap framework theme that naturally scales to tablet and mobile screens."
      });
    } else {
      passed.push({
        title: "Mobile Viewport Optimization Pass",
        desc: "Your site layout is fully adaptive, which is required for responsive AdSense display units."
      });
    }

    if (!isCustomDomain) {
      score -= 25;
      criticalFails.push({
        title: "Subdomain or Free Hosting Limit",
        desc: "Using a subdomain (e.g. mypage.wixsite.com or page.github.io) is heavily penalized by AdSense unless using Google's Blogger network directly.",
        fix: "Purchase and map a custom TLD (like .com, .org, or .net) to represent a serious long-term brand asset."
      });
    } else {
      passed.push({
        title: "Top-Level Custom Domain Active",
        desc: "A custom domain shows authority and makes ads.txt deployment incredibly straightforward."
      });
    }

    if (!isHttps) {
      score -= 15;
      criticalFails.push({
        title: "Insecure Protocol (No HTTPS)",
        desc: "Google requires all partner sites displaying ads to run over secure SSL (HTTPS).",
        fix: "Install a free Let's Encrypt SSL certificate through your registrar, host, or Cloudflare DNS layer."
      });
    } else {
      passed.push({
        title: "HTTPS Security Enabled",
        desc: "SSL certificate is valid and encrypted, satisfying baseline ad safety requirements."
      });
    }

    // --- Traffic & Age ---
    if (trafficSource === "bots" || trafficSource === "paid") {
      score -= 30;
      criticalFails.push({
        title: "Unapproved Traffic Sources",
        desc: "AdSense forbids traffic sources derived from bots, paid clicking schemes, or spam social groups.",
        fix: "Focus solely on organic traffic via quality content creation. Do not use artificial traffic bots or aggressive paid-click loops."
      });
    } else if (trafficSource === "social") {
      warnings.push({
        title: "Social Media Traffic Dominated",
        desc: "While social media clicks are acceptable, AdSense accounts can get limited or banned if there isn't a robust base of organic search traffic.",
        fix: "Integrate proper SEO practices, focusing on long-tail search terms to organic search hits from Google, Bing, and DuckDuckGo."
      });
    } else {
      passed.push({
        title: "Healthy Traffic Pipeline Pattern",
        desc: "Primary focus on organic search minimizes programmatic fraud triggers."
      });
    }

    if (siteAge === "new") {
      score -= 15;
      warnings.push({
        title: "Domain Age Under 1 Month",
        desc: "Brand new domains are heavily audited and frequently rejected. In certain locations (like India, China, and parts of Europe), a minimum age of 6 months is required.",
        fix: "Focus on adding high-quality content weekly and wait until your domain is at least 3 months old to register for AdSense review."
      });
    } else if (siteAge === "medium") {
      passed.push({
        title: "Acceptable Domain Age Check",
        desc: "Your site is 1-6 months old. Ensure content is steady so reviewers see active maintenance."
      });
    } else {
      passed.push({
        title: "Well-Established Brand Age",
        desc: "Your site is older than 6 months, bypassing any geographic or domain-trust age filters."
      });
    }

    // Ensure score does not drop below 0 or exceed 100
    const finalScore = Math.max(0, Math.min(100, score));

    return { score: finalScore, passed, warnings, criticalFails };
  };

  const results = calculateAuditResults();

  // Generate compliant Privacy Policy based on inputs
  const handleGeneratePrivacy = () => {
    const today = new Date().toLocaleDateString("en-US", { year: 'numeric', month: 'long', day: 'numeric' });
    const template = `# Privacy Policy for ${siteName}

**Last Updated: ${today}**

At ${siteName}, accessible from https://${domain}, one of our main priorities is the privacy of our visitors. This Privacy Policy document contains types of information that is collected and recorded by ${siteName} and how we use it.

If you have additional questions or require more information about our Privacy Policy, do not hesitate to contact us.

This Privacy Policy applies only to our online activities and is valid for visitors to our website with regards to the information that they shared and/or collect in ${siteName}. This policy is not applicable to any information collected offline or via channels other than this website.

## Consent
By using our website, you hereby consent to our Privacy Policy and agree to its terms.

## Information We Collect
The personal information that you are asked to provide, and the reasons why you are asked to provide it, will be made clear to you at the point we ask you to provide your personal information.

If you contact us directly, we may receive additional information about you such as your name, email address, phone number, the contents of the message and/or attachments you may send us, and any other information you may choose to provide.

## How We Use Your Information
We use the information we collect in various ways, including to:
- Provide, operate, and maintain our website
- Improve, personalize, and expand our website
- Understand and analyze how you use our website
- Develop new products, services, features, and functionality
- Communicate with you, either directly or through one of our partners
- Send you emails
- Find and prevent fraud

## Log Files
${siteName} follows a standard procedure of using log files. These files log visitors when they visit websites. All hosting companies do this and a part of hosting services' analytics. The information collected by log files include internet protocol (IP) addresses, browser type, Internet Service Provider (ISP), date and time stamp, referring/exit pages, and possibly the number of clicks. These are not linked to any information that is personally identifiable. The purpose of the information is for analyzing trends, administering the site, tracking users' movement on the website, and gathering demographic information.

## Google DoubleClick DART Cookie & Third-Party Advertising (Mandatory for AdSense)
Google is one of a third-party vendor on our site. It also uses cookies, known as DART cookies, to serve ads to our site visitors based upon their visit to www.website.com and other sites on the internet. However, visitors may choose to decline the use of DART cookies by visiting the Google ad and content network Privacy Policy at the following URL – https://policies.google.com/technologies/ads

Some of advertisers on our site may use cookies and web beacons. Our advertising partners include:
- **Google AdSense**

These third-party ad servers or ad networks use technology in their respective advertisements and links that appear on ${siteName}, which are sent directly to users' browsers. They automatically receive your IP address when this occurs. These technologies are used to measure the effectiveness of their advertising campaigns and/or to personalize the advertising content that you see on websites that you visit.

Please note that ${siteName} has no access to or control over these cookies that are used by third-party advertisers.

## Third Party Privacy Policies
${siteName}'s Privacy Policy does not apply to other advertisers or websites. Thus, we are advising you to consult the respective Privacy Policies of these third-party ad servers for more detailed information. It may include their practices and instructions about how to opt-out of certain options.

You can choose to disable cookies through your individual browser options. To know more detailed information about cookie management with specific web browsers, it can be found at the browsers' respective websites.

${includeGdpr ? `## GDPR Data Protection Rights
We want to make sure you are fully aware of all of your data protection rights. Every user is entitled to the following:
- **The right to access** – You have the right to request copies of your personal data.
- **The right to rectification** – You have the right to request that we correct any information you believe is inaccurate.
- **The right to erasure** – You have the right to request that we erase your personal data, under certain conditions.
- **The right to restrict processing** – You have the right to request that we restrict the processing of your personal data, under certain conditions.
- **The right to object to processing** – You have the right to object to our processing of your personal data, under certain conditions.
- **The right to data portability** – You have the right to request that we transfer the data that we have collected to another organization, or directly to you, under certain conditions.

## CCPA Privacy Rights (Do Not Sell My Personal Information)
Under the CCPA, among other rights, California consumers have the right to:
- Request that a business that collects a consumer's personal data disclose the categories and specific pieces of personal data that a business has collected about consumers.
- Request that a business delete any personal data about the consumer that a business has collected.
- Request that a business that sells a consumer's personal data, not sell the consumer's personal data.
` : ""}

## Contact Information
If you have questions or complaints regarding this Privacy Policy, please contact us at:
- **Email:** ${contactEmail}
- **Website Owner:** ${ownerName}
`;
    setGeneratedPrivacyText(template);
  };

  const generatedAdsTxt = `google.com, ${pubId}, DIRECT, f08c47fec0942fa0`;

  return (
    <ToolPageLayout
      title="Google AdSense Approval Audit & Policy Checker"
      description="Audit your website's elements, content, pages, and code structures to identify compliance issues, calculate your approval probability score, and download ready-to-publish legal templates."
      h1="AdSense Approval Auditor"
      category="Web Dev & Network"
      categoryPath="/web-dev-network"
      howToUse={[
        "Fill out our guided diagnostic checklist regarding your website's content, structure, and legal page layouts.",
        "Click 'Analyze Site Compliance' to simulate a comprehensive Google AdSense program policy audit.",
        "Inspect your final score and detailed scorecard to view passes, warnings, and critical fixes.",
        "Use our built-in generators to build compliant Privacy Policies, custom editorial bios, and ads.txt configurations instantly."
      ]}
      faq={[
        { question: "What are the most common reasons Google rejects AdSense applications?", answer: "The three most frequent rejection triggers are: 'Valueless inventory' (thin content, duplicate text, or AI-generated copy), 'Navigation difficulties' (broken routes, messy structures), and 'Missing required policies' (no Privacy Policy with cookie disclosures)." },
        { question: "Is a Privacy Policy mandatory to get approved by AdSense?", answer: "Yes, absolutely. Google's program policies legally require partners to host a Privacy Policy detailing cookie handling, personalized tracking systems, and direct opt-outs for the Google DoubleClick DART cookies." },
        { question: "Can I get approved using AI-generated content?", answer: "Yes, but only if the AI content is thoroughly edited, fact-checked, and augmented with personal authority and unique value. Raw, copy-pasted ChatGPT lists are consistently rejected under the 'Low-Value Content' standard." }
      ]}
    >
      {/* Tab Navigation */}
      <div className="flex border-b border-slate-200 mb-8 overflow-x-auto whitespace-nowrap scrollbar-none">
        <button
          onClick={() => setActiveTab("audit")}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "audit" 
              ? "border-blue-600 text-blue-600 font-semibold" 
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Search className="w-4 h-4" />
          1. Site Diagnostic
        </button>
        <button
          onClick={() => setActiveTab("report")}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "report" 
              ? "border-blue-600 text-blue-600 font-semibold" 
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <CheckCircle2 className="w-4 h-4" />
          2. Audit Report Card
        </button>
        <button
          onClick={() => {
            setActiveTab("privacy-gen");
            if (!generatedPrivacyText) handleGeneratePrivacy();
          }}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "privacy-gen" 
              ? "border-blue-600 text-blue-600 font-semibold" 
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <FileText className="w-4 h-4" />
          3. AdSense Privacy Generator
        </button>
        <button
          onClick={() => setActiveTab("ads-txt")}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "ads-txt" 
              ? "border-blue-600 text-blue-600 font-semibold" 
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <Code className="w-4 h-4" />
          4. ads.txt Builder
        </button>
        <button
          onClick={() => setActiveTab("policies")}
          className={`px-4 py-3 font-medium text-sm border-b-2 transition-all flex items-center gap-2 ${
            activeTab === "policies" 
              ? "border-blue-600 text-blue-600 font-semibold" 
              : "border-transparent text-slate-500 hover:text-slate-800"
          }`}
        >
          <BookOpen className="w-4 h-4" />
          Policy Guide
        </button>
      </div>

      {/* Main Container */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden p-6">
        
        {/* TAB 1: SITE DIAGNOSTIC */}
        {activeTab === "audit" && (
          <div className="space-y-8">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Globe className="text-blue-500 w-5 h-5" />
                Step 1: Website Profile & Setup
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Enter your basic site settings to configure our compliance scanning parameters.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="domain">Your Website Domain</Label>
                <div className="flex rounded-md shadow-sm">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-slate-300 bg-slate-50 text-slate-500 text-sm">
                    https://
                  </span>
                  <Input
                    id="domain"
                    type="text"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value.replace(/^(https?:\/\/)?(www\.)?/, ""))}
                    placeholder="myawesomeblog.com"
                    className="rounded-l-none"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="niche">Publishing Niche</Label>
                <select
                  id="niche"
                  value={siteNiche}
                  onChange={(e) => setSiteNiche(e.target.value)}
                  className="w-full h-10 px-3 py-2 bg-white border border-slate-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="technology">Tech News & Coding Guides</option>
                  <option value="recipe">Food & Culinary Blog</option>
                  <option value="finance">Finance, Crypto & Investment</option>
                  <option value="lifestyle">Lifestyle, Fashion & Travel</option>
                  <option value="health">General Wellness & Fitness</option>
                  <option value="tools">Online Web Tools / Calculators</option>
                  <option value="downloads">Software / App Downloads</option>
                  <option value="other">Other / Personal Journal</option>
                </select>
              </div>
            </div>

            {/* Diagnostic Categories Grid */}
            <div className="grid md:grid-cols-2 gap-8 pt-4">
              {/* Box A: Content & Integrity */}
              <div className="bg-slate-50 rounded-lg p-5 border border-slate-100 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 border-b border-slate-200 pb-2">
                  <span className="text-blue-600 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">A</span>
                  Content & Author Integrity
                </h3>

                <div className="space-y-3 text-sm">
                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Content Originality Source</Label>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => setContentOrigin("original")}
                        className={`p-2 rounded border text-left text-xs transition-all ${
                          contentOrigin === "original" ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                        }`}
                      >
                        ✍️ 100% Handcrafted
                      </button>
                      <button
                        type="button"
                        onClick={() => setContentOrigin("ai")}
                        className={`p-2 rounded border text-left text-xs transition-all ${
                          contentOrigin === "ai" ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                        }`}
                      >
                        🤖 Raw/Slightly Edited AI
                      </button>
                      <button
                        type="button"
                        onClick={() => setContentOrigin("curated")}
                        className={`p-2 rounded border text-left text-xs transition-all ${
                          contentOrigin === "curated" ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                        }`}
                      >
                        📰 Curated News / Feed
                      </button>
                      <button
                        type="button"
                        onClick={() => setContentOrigin("scraped")}
                        className={`p-2 rounded border text-left text-xs transition-all ${
                          contentOrigin === "scraped" ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                        }`}
                      >
                        🔗 Copied / Translated
                      </button>
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Average Word Count per Post</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["high", "medium", "low"] as const).map((level) => {
                        const labels = { high: ">800 words", medium: "400-800 words", low: "<400 words" };
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setWordCount(level)}
                            className={`p-2 rounded border text-center text-xs transition-all ${
                              wordCount === level ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                            }`}
                          >
                            {labels[level]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Total Published Articles/Pages</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["high", "medium", "low"] as const).map((level) => {
                        const labels = { high: ">30 posts", medium: "10-30 posts", low: "<10 posts" };
                        return (
                          <button
                            key={level}
                            type="button"
                            onClick={() => setArticleCount(level)}
                            className={`p-2 rounded border text-center text-xs transition-all ${
                              articleCount === level ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                            }`}
                          >
                            {labels[level]}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-center justify-between">
                      <Label htmlFor="restrictedNiche" className="text-xs cursor-pointer">Restricted niches (adult/hacking/medical advice)?</Label>
                      <input
                        id="restrictedNiche"
                        type="checkbox"
                        checked={restrictedNiche}
                        onChange={(e) => setRestrictedNiche(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex items-center justify-between">
                      <Label htmlFor="placeholder" className="text-xs cursor-pointer">Has draft lorem-ipsum dummy pages?</Label>
                      <input
                        id="placeholder"
                        type="checkbox"
                        checked={hasPlaceholderText}
                        onChange={(e) => setHasPlaceholderText(e.target.checked)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Box B: Navigation & UX */}
              <div className="bg-slate-50 rounded-lg p-5 border border-slate-100 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 border-b border-slate-200 pb-2">
                  <span className="text-blue-600 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">B</span>
                  Navigation, Menus & UX
                </h3>

                <div className="space-y-4 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="hasMenu" className="text-xs font-medium cursor-pointer">Header Navigation Menu Active?</Label>
                      <p className="text-[11px] text-slate-400">Links to categories, legal, and home</p>
                    </div>
                    <input
                      id="hasMenu"
                      type="checkbox"
                      checked={hasMenu}
                      onChange={(e) => setHasMenu(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="responsive" className="text-xs font-medium cursor-pointer">Mobile-Responsive Viewports?</Label>
                      <p className="text-[11px] text-slate-400">Adapts layout flawlessly to screen sizes</p>
                    </div>
                    <input
                      id="responsive"
                      type="checkbox"
                      checked={isMobileResponsive}
                      onChange={(e) => setIsMobileResponsive(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="search" className="text-xs font-medium cursor-pointer">Active Site Search / Tags?</Label>
                      <p className="text-[11px] text-slate-400">Users can search internal articles</p>
                    </div>
                    <input
                      id="search"
                      type="checkbox"
                      checked={hasSearch}
                      onChange={(e) => setHasSearch(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="broken" className="text-xs font-medium cursor-pointer">Has Broken Links (404 errors)?</Label>
                      <p className="text-[11px] text-slate-400">Dead sidebar elements, missing images</p>
                    </div>
                    <input
                      id="broken"
                      type="checkbox"
                      checked={hasBrokenLinks}
                      onChange={(e) => setHasBrokenLinks(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Box C: Mandatory Legal Pages */}
              <div className="bg-slate-50 rounded-lg p-5 border border-slate-100 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 border-b border-slate-200 pb-2">
                  <span className="text-blue-600 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">C</span>
                  Legal Compliance Pages
                </h3>

                <p className="text-xs text-slate-500 italic pb-1">
                  💡 AdSense reviewer audits fail immediately if required pages aren't linked clearly in the footer or header menu.
                </p>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div className="flex items-center gap-2 bg-white p-2.5 rounded border border-slate-200">
                    <input
                      id="hasPrivacy"
                      type="checkbox"
                      checked={hasPrivacyPolicy}
                      onChange={(e) => setHasPrivacyPolicy(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="hasPrivacy" className="text-xs font-medium cursor-pointer">Privacy Policy</Label>
                  </div>

                  <div className="flex items-center gap-2 bg-white p-2.5 rounded border border-slate-200">
                    <input
                      id="hasTerms"
                      type="checkbox"
                      checked={hasTerms}
                      onChange={(e) => setHasTerms(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="hasTerms" className="text-xs font-medium cursor-pointer">Terms of Service</Label>
                  </div>

                  <div className="flex items-center gap-2 bg-white p-2.5 rounded border border-slate-200">
                    <input
                      id="hasContact"
                      type="checkbox"
                      checked={hasContact}
                      onChange={(e) => setHasContact(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="hasContact" className="text-xs font-medium cursor-pointer">Contact Us Page</Label>
                  </div>

                  <div className="flex items-center gap-2 bg-white p-2.5 rounded border border-slate-200">
                    <input
                      id="hasAbout"
                      type="checkbox"
                      checked={hasAbout}
                      onChange={(e) => setHasAbout(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <Label htmlFor="hasAbout" className="text-xs font-medium cursor-pointer">About Us Page</Label>
                  </div>
                </div>
              </div>

              {/* Box D: Domain & Traffic */}
              <div className="bg-slate-50 rounded-lg p-5 border border-slate-100 space-y-4">
                <h3 className="font-semibold text-slate-900 flex items-center gap-2 border-b border-slate-200 pb-2">
                  <span className="text-blue-600 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold">D</span>
                  Technical Infrastructure & Traffic
                </h3>

                <div className="space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="tld" className="text-xs font-medium cursor-pointer">Top-Level Domain Custom Mapping?</Label>
                      <p className="text-[11px] text-slate-400">Using .com/.net/.org (no free subdomains)</p>
                    </div>
                    <input
                      id="tld"
                      type="checkbox"
                      checked={isCustomDomain}
                      onChange={(e) => setIsCustomDomain(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <div>
                      <Label htmlFor="ssl" className="text-xs font-medium cursor-pointer">HTTPS Security Standard Enabled?</Label>
                      <p className="text-[11px] text-slate-400">SSL certified with secure connections</p>
                    </div>
                    <input
                      id="ssl"
                      type="checkbox"
                      checked={isHttps}
                      onChange={(e) => setIsHttps(e.target.checked)}
                      className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="space-y-1.5 pt-1">
                    <Label className="text-xs text-slate-600">Primary Traffic Channels</Label>
                    <select
                      value={trafficSource}
                      onChange={(e) => setTrafficSource(e.target.value as any)}
                      className="w-full h-8 px-2 bg-white border border-slate-300 rounded text-xs focus:outline-none"
                    >
                      <option value="search">🔍 Organic Search Engine Visitors</option>
                      <option value="social">📱 Social Media Clicks / Profiles</option>
                      <option value="paid">💳 PPC / Paid Campaigns</option>
                      <option value="bots">🤖 Autopilot/Bot traffic</option>
                    </select>
                  </div>

                  <div className="space-y-1.5">
                    <Label className="text-xs text-slate-600">Active Domain Age</Label>
                    <div className="grid grid-cols-3 gap-2">
                      {(["old", "medium", "new"] as const).map((age) => {
                        const labels = { old: ">6 Months", medium: "1-6 Months", new: "<1 Month" };
                        return (
                          <button
                            key={age}
                            type="button"
                            onClick={() => setSiteAge(age)}
                            className={`py-1 rounded border text-center text-xs transition-all ${
                              siteAge === age ? "bg-blue-50 border-blue-400 font-medium text-blue-700" : "bg-white border-slate-200 text-slate-600"
                            }`}
                          >
                            {labels[age]}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Launch Action */}
            <div className="flex flex-col items-center justify-center pt-6 border-t border-slate-100">
              {isAuditing ? (
                <div className="w-full max-w-md space-y-3">
                  <div className="flex items-center justify-between text-xs text-slate-600">
                    <span className="font-mono">{auditMessage}</span>
                    <span>{Math.round(auditProgress)}%</span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <motion.div
                      className="bg-blue-600 h-full rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${auditProgress}%` }}
                      transition={{ duration: 0.1 }}
                    />
                  </div>
                  <p className="text-center text-[11px] text-slate-400 italic">Evaluating site headers and content databases...</p>
                </div>
              ) : (
                <Button
                  onClick={runAudit}
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow-md flex items-center gap-2 transform active:scale-95 transition-all"
                >
                  <Sparkles className="w-5 h-5 animate-pulse" />
                  Analyze Site Compliance
                </Button>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: AUDIT REPORT CARD */}
        {activeTab === "report" && (
          <div className="space-y-8">
            <div className="border-b border-slate-100 pb-4 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                  <ShieldCheck className="text-emerald-500 w-5 h-5" />
                  Site Compliance Audit Report
                </h2>
                <p className="text-sm text-slate-500 mt-1">
                  Analysis result for website: <span className="font-mono text-blue-600 font-semibold underline">{domain}</span>
                </p>
              </div>
              <Button
                onClick={() => setActiveTab("audit")}
                variant="outline"
                size="sm"
                className="flex items-center gap-1.5"
              >
                <RefreshCw className="w-4 h-4" />
                Re-Run Diagnostic
              </Button>
            </div>

            {/* Scoreboard display */}
            <div className="grid md:grid-cols-3 gap-6">
              <div className="md:col-span-1 bg-slate-50 border border-slate-200/60 rounded-xl p-6 flex flex-col items-center justify-center text-center space-y-4">
                <span className="text-xs uppercase font-bold tracking-wider text-slate-400">Approval Probability</span>
                
                {/* Visual Radial Dial simulation */}
                <div className="relative flex items-center justify-center">
                  <svg className="w-36 h-36">
                    <circle
                      className="text-slate-200"
                      strokeWidth="10"
                      stroke="currentColor"
                      fill="transparent"
                      r="60"
                      cx="72"
                      cy="72"
                    />
                    <circle
                      className={
                        results.score >= 80 
                          ? "text-emerald-500" 
                          : results.score >= 50 
                            ? "text-yellow-500" 
                            : "text-red-500"
                      }
                      strokeWidth="10"
                      strokeDasharray={377}
                      strokeDashoffset={377 - (377 * results.score) / 100}
                      strokeLinecap="round"
                      stroke="currentColor"
                      fill="transparent"
                      r="60"
                      cx="72"
                      cy="72"
                      transform="rotate(-90 72 72)"
                    />
                  </svg>
                  <div className="absolute flex flex-col items-center justify-center">
                    <span className="text-4xl font-extrabold tracking-tight font-mono text-slate-800">
                      {results.score}%
                    </span>
                    <span className="text-[10px] font-semibold text-slate-500 mt-0.5">SCORE</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className={`text-sm font-bold ${
                    results.score >= 80 
                      ? "text-emerald-600" 
                      : results.score >= 50 
                        ? "text-yellow-600" 
                        : "text-red-600"
                  }`}>
                    {results.score >= 80 
                      ? "High Approval Rate" 
                      : results.score >= 50 
                        ? "Medium Risk Violation" 
                        : "Immediate Reject Expected"
                    }
                  </span>
                  <p className="text-xs text-slate-400 px-4">
                    Based on typical Google reviewer evaluation guidelines.
                  </p>
                </div>
              </div>

              {/* Status breakdown numbers */}
              <div className="md:col-span-2 grid sm:grid-cols-3 gap-4">
                <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-5 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-bold text-emerald-800 uppercase tracking-wider block">Passed Checks</span>
                    <span className="text-4xl font-black font-mono text-emerald-600 block mt-2">{results.passed.length}</span>
                  </div>
                  <p className="text-[11px] text-emerald-700/80 mt-4 leading-relaxed">Compliance criteria currently satisfied by your web structure.</p>
                </div>

                <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-5 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-bold text-yellow-800 uppercase tracking-wider block">Warnings</span>
                    <span className="text-4xl font-black font-mono text-yellow-600 block mt-2">{results.warnings.length}</span>
                  </div>
                  <p className="text-[11px] text-yellow-700/80 mt-4 leading-relaxed">Minor violations that could lead to thin content or high-bounce flags.</p>
                </div>

                <div className="bg-red-50 border border-red-100 rounded-xl p-5 flex flex-col justify-between">
                  <div>
                    <span className="text-xs font-bold text-red-800 uppercase tracking-wider block">Critical Fails</span>
                    <span className="text-4xl font-black font-mono text-red-600 block mt-2">{results.criticalFails.length}</span>
                  </div>
                  <p className="text-[11px] text-red-700/80 mt-4 leading-relaxed">Fatal violations of AdSense policy causing instant rejection.</p>
                </div>
              </div>
            </div>

            {/* List of Critical Failures to FIX */}
            {results.criticalFails.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-base font-bold text-red-600 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 animate-bounce" />
                  Critical Issues to Fix Before Applying ({results.criticalFails.length})
                </h3>
                
                <div className="space-y-3">
                  {results.criticalFails.map((fail, idx) => (
                    <div key={idx} className="bg-white border-l-4 border-red-500 rounded-r-lg border border-slate-200/80 p-4 shadow-xs">
                      <div className="flex gap-3">
                        <XCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-slate-800 text-sm">{fail.title}</h4>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{fail.desc}</p>
                          <div className="mt-3 bg-red-50/50 p-2.5 rounded border border-red-100 text-xs text-slate-700">
                            <strong className="text-red-700">Action Plan: </strong>
                            {fail.fix}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* List of Warning Parameters */}
            {results.warnings.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-base font-bold text-yellow-600 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5" />
                  Warnings & Recommendations ({results.warnings.length})
                </h3>

                <div className="space-y-3">
                  {results.warnings.map((warn, idx) => (
                    <div key={idx} className="bg-white border-l-4 border-yellow-500 rounded-r-lg border border-slate-200/80 p-4 shadow-xs">
                      <div className="flex gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0 mt-0.5" />
                        <div>
                          <h4 className="font-bold text-slate-800 text-sm">{warn.title}</h4>
                          <p className="text-xs text-slate-500 mt-1 leading-relaxed">{warn.desc}</p>
                          <div className="mt-2 text-xs text-slate-700">
                            <strong className="text-yellow-700">Improvement: </strong>
                            {warn.fix}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* List of Passed parameters */}
            {results.passed.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-base font-bold text-slate-700 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                  Successfully Satisfied Requirements ({results.passed.length})
                </h3>

                <div className="grid sm:grid-cols-2 gap-3">
                  {results.passed.map((pass, idx) => (
                    <div key={idx} className="bg-slate-50 border border-slate-200/60 rounded-lg p-3 flex gap-3.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-semibold text-slate-800 text-xs">{pass.title}</h4>
                        <p className="text-[11px] text-slate-500 mt-0.5">{pass.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA to legal page builders */}
            {!hasPrivacyPolicy && (
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                <div className="space-y-1 text-center sm:text-left">
                  <h4 className="font-bold text-blue-900 text-sm flex items-center gap-2 justify-center sm:justify-start">
                    <FileText className="w-4 h-4 text-blue-500" />
                    Crucial Warning: Fix the Missing Privacy Policy Now
                  </h4>
                  <p className="text-xs text-blue-700">
                    Your diagnostic indicates no active Privacy Policy. AdSense mandates a specific cookie policy template.
                  </p>
                </div>
                <Button
                  onClick={() => {
                    setActiveTab("privacy-gen");
                    handleGeneratePrivacy();
                  }}
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs shrink-0"
                >
                  Generate Compliant Privacy Policy
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* TAB 3: PRIVACY POLICY GENERATOR */}
        {activeTab === "privacy-gen" && (
          <div className="space-y-6">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <FileText className="text-blue-500 w-5 h-5" />
                Google AdSense Compliant Privacy Policy Generator
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Generate a ready-to-publish Privacy Policy featuring mandatory DoubleClick DART, cookie data collection, and GDPR/CCPA clauses.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Form inputs */}
              <div className="space-y-4 md:col-span-1 bg-slate-50 p-4 rounded-lg border border-slate-200/80">
                <h3 className="font-semibold text-xs uppercase tracking-wider text-slate-500">Document Settings</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="gen-domain">Domain Name</Label>
                  <Input
                    id="gen-domain"
                    type="text"
                    value={domain}
                    onChange={(e) => {
                      setDomain(e.target.value);
                      handleGeneratePrivacy();
                    }}
                    placeholder="myawesomeblog.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gen-site">Site Brand Name</Label>
                  <Input
                    id="gen-site"
                    type="text"
                    value={siteName}
                    onChange={(e) => {
                      setSiteName(e.target.value);
                      handleGeneratePrivacy();
                    }}
                    placeholder="My Awesome Blog"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gen-owner">Owner/Company Name</Label>
                  <Input
                    id="gen-owner"
                    type="text"
                    value={ownerName}
                    onChange={(e) => {
                      setOwnerName(e.target.value);
                      handleGeneratePrivacy();
                    }}
                    placeholder="Awesome Media Group"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="gen-email">Contact Email Address</Label>
                  <Input
                    id="gen-email"
                    type="email"
                    value={contactEmail}
                    onChange={(e) => {
                      setContactEmail(e.target.value);
                      handleGeneratePrivacy();
                    }}
                    placeholder="contact@domain.com"
                  />
                </div>

                <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                  <input
                    id="gdpr"
                    type="checkbox"
                    checked={includeGdpr}
                    onChange={(e) => {
                      setIncludeGdpr(e.target.checked);
                      handleGeneratePrivacy();
                    }}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600"
                  />
                  <Label htmlFor="gdpr" className="text-xs font-semibold cursor-pointer">Include GDPR & CCPA Sections</Label>
                </div>

                <Button
                  onClick={handleGeneratePrivacy}
                  className="w-full bg-blue-600 text-white text-xs mt-4"
                >
                  Refresh Generated Document
                </Button>
              </div>

              {/* Text viewer */}
              <div className="md:col-span-2 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Generated Markdown</span>
                  <Button
                    onClick={() => handleCopy(generatedPrivacyText, "privacy")}
                    variant="outline"
                    size="sm"
                    className="h-8 text-xs flex items-center gap-1 bg-white border-slate-200 shadow-sm"
                  >
                    {isCopied ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-500" />
                        Copied Markdown!
                      </>
                    ) : (
                      <>
                        <Clipboard className="w-3.5 h-3.5" />
                        Copy Markdown Code
                      </>
                    )}
                  </Button>
                </div>

                <div className="relative">
                  <Textarea
                    readOnly
                    rows={18}
                    value={generatedPrivacyText}
                    className="font-mono text-xs bg-slate-900 text-slate-100 p-4 border-slate-800 rounded-lg whitespace-pre focus:ring-0 leading-relaxed overflow-y-auto"
                  />
                </div>
                <p className="text-[11px] text-slate-400 leading-relaxed italic">
                  ⚠️ Note: After copying, create a new page named "Privacy Policy" on your website CMS (WordPress, Ghost, Hugo, etc.), paste this text content, and make sure to place a visible link in your footer menu.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: ADS.TXT GENERATOR */}
        {activeTab === "ads-txt" && (
          <div className="space-y-6">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <Code className="text-blue-500 w-5 h-5" />
                Publisher ads.txt File Builder
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Create a valid authorized digital seller profile (ads.txt) config snippet for Google AdSense to avoid revenue loss warnings.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              <div className="space-y-4 md:col-span-1 bg-slate-50 p-5 rounded-lg border border-slate-200">
                <h3 className="font-bold text-xs uppercase tracking-wider text-slate-500">Publisher Settings</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="pubid">AdSense Publisher ID</Label>
                  <Input
                    id="pubid"
                    type="text"
                    value={pubId}
                    onChange={(e) => setPubId(e.target.value.trim())}
                    placeholder="pub-XXXXXXXXXXXXXXXX"
                  />
                  <p className="text-[11px] text-slate-400 leading-normal">
                    Format must be: <code className="font-mono font-bold bg-slate-200/60 px-1 py-0.5 rounded text-slate-700">pub-</code> followed by exactly 16 numeric digits.
                  </p>
                </div>
              </div>

              <div className="md:col-span-2 space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs uppercase font-bold tracking-wider text-slate-400">File Output: ads.txt</Label>
                    <Button
                      onClick={() => handleCopy(generatedAdsTxt, "adstxt")}
                      size="sm"
                      variant="outline"
                      className="h-8 text-xs flex items-center gap-1.5"
                    >
                      {adsTxtCopied ? (
                        <>
                          <Check className="w-3.5 h-3.5 text-emerald-500" />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Clipboard className="w-3.5 h-3.5" />
                          Copy Config Line
                        </>
                      )}
                    </Button>
                  </div>

                  <div className="bg-slate-950 text-slate-100 p-4 rounded-lg font-mono text-xs border border-slate-800 tracking-wide select-all">
                    {generatedAdsTxt}
                  </div>
                </div>

                <div className="bg-blue-50/50 border border-blue-100 rounded-lg p-4 space-y-2 text-xs text-slate-700 leading-relaxed">
                  <h4 className="font-bold text-blue-900">How to deploy this ads.txt:</h4>
                  <ol className="list-decimal pl-4 space-y-1">
                    <li>Create a plain text file named <code className="font-mono bg-blue-100/60 px-1 py-0.5 rounded text-blue-800 font-bold">ads.txt</code> on your local computer.</li>
                    <li>Paste the single line of config displayed above into the text file.</li>
                    <li>Upload this file to the root directory of your website so it is accessible publicly at: <code className="font-mono font-bold text-blue-900 bg-blue-100/60 px-1 py-0.5 rounded">https://{domain}/ads.txt</code></li>
                    <li>It can take up to 24-48 hours for Google to crawl and validate the file once uploaded.</li>
                  </ol>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 5: POLICY EXPLORER */}
        {activeTab === "policies" && (
          <div className="space-y-6">
            <div className="border-b border-slate-100 pb-4">
              <h2 className="text-xl font-semibold text-slate-900 flex items-center gap-2">
                <BookOpen className="text-blue-500 w-5 h-5" />
                Google AdSense program policies & rules handbook
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                Demystifying the complex guidelines to help your site pass manual reviewer checks on the first attempt.
              </p>
            </div>

            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-bold text-sm text-slate-800 mb-1">What is "Valueless Inventory" / "Low Value Content"?</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  This is the single most common rejection criteria. It means Google's reviewers evaluated your site and found that it does not offer users sufficient unique or beneficial information compared to thousands of existing resources. To fix this:
                </p>
                <ul className="list-disc pl-4 mt-2 space-y-1 text-xs text-slate-500">
                  <li>Avoid thin pages that contain only a couple of short paragraphs of basic definitions.</li>
                  <li>Ensure your articles answer complex questions with structured sections, tables, lists, and unique images.</li>
                  <li>Incorporate personal authority, expert insights, and real-world trials/opinions (E-E-A-T parameter).</li>
                </ul>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-bold text-sm text-slate-800 mb-1">Navigation & Menus Best Practices</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Google's program policies state that sites must have clear, simple, and functional navigational pathways. If the human reviewer struggles to locate your articles, or gets lost because of broken menus, they will issue a refusal.
                </p>
                <ul className="list-disc pl-4 mt-2 space-y-1 text-xs text-slate-500">
                  <li>Keep a persistent navigation header menu visible on both desktop and mobile layouts.</li>
                  <li>Create categorized directory headers linking directly to topic lists.</li>
                  <li>Link your required contact and privacy policy pages directly in a clear site footer.</li>
                </ul>
              </div>

              <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                <h3 className="font-bold text-sm text-slate-800 mb-1">Restricted and Prohibited Content Niches</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Google will immediately blacklist and reject applications targeting forbidden content guidelines. The following content niches will get your account permanently unapproved or terminated:
                </p>
                <div className="grid sm:grid-cols-2 gap-4 mt-3">
                  <div className="bg-red-50/50 p-2 rounded border border-red-100 text-xs text-red-800 font-medium">
                    ❌ Prohibited Topics:
                    <ul className="list-disc pl-4 mt-1 text-[11px] text-red-600/90 font-normal space-y-0.5">
                      <li>Adult & sexually suggestive materials.</li>
                      <li>Hacking, cracking, or security bypass tutorials.</li>
                      <li>Illegal drugs, weapons, or violent content.</li>
                      <li>Counterfeit, trademarked, or replica merchandise.</li>
                    </ul>
                  </div>
                  <div className="bg-emerald-50/50 p-2 rounded border border-emerald-100 text-xs text-emerald-800 font-medium">
                    ✅ Safe & Approved Topics:
                    <ul className="list-disc pl-4 mt-1 text-[11px] text-emerald-600/90 font-normal space-y-0.5">
                      <li>Coding tutorials, tech tips, and product reviews.</li>
                      <li>Culinary recipes, kitchen advice, and food blogs.</li>
                      <li>Travel guides, destination reviews, and itineraries.</li>
                      <li>Personal development, workout schedules, and home decor.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </ToolPageLayout>
  );
}
